import { Plus, Trash2, X } from "lucide-react";
import { formatINR } from "../lib/api";
import { COMPANY_BRANDS } from "../lib/brands";
import { DirectoryPicker } from "./DirectoryPicker";

export function InvoiceFormModal({ form, setForm, recalc, updateItem, addItem, removeItem, toggleLR, pickCustomer, unbilled, customers, editingId, saving, save, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center overflow-y-auto py-8" data-testid="invoice-form-modal">
      <div className="bg-white w-full max-w-5xl mx-4">
        <div className="px-6 py-4 border-b border-[#E5E3DB] flex items-center justify-between">
          <h2 className="font-headings text-xl font-bold">{editingId ? "Edit Invoice" : "Create Invoice"}</h2>
          <button className="btn-ghost" onClick={onClose} data-testid="invoice-form-close"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-6">
          <div>
            <div className="section-title">Invoice</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div><label className="input-label">Invoice Number {editingId ? "(edit to renumber/transfer)" : "(blank = auto)"}</label><input className="input-field font-mono" value={form.invoice_number || ""} onChange={(e)=>setForm({...form, invoice_number: e.target.value})} placeholder={editingId ? "" : "Auto-generate"} data-testid="invoice-number-input" /></div>
              <div><label className="input-label">Date</label><input type="date" className="input-field" value={form.invoice_date} onChange={(e)=>setForm({...form, invoice_date: e.target.value})} data-testid="invoice-date" /></div>
              <div><label className="input-label">Due Date</label><input type="date" className="input-field" value={form.due_date} onChange={(e)=>setForm({...form, due_date: e.target.value})} data-testid="invoice-due-date" /></div>
              <div><label className="input-label">GST %</label><input type="number" step="0.01" className="input-field" value={form.gst_percent} onChange={(e)=>setForm(recalc({...form, gst_percent: Number(e.target.value)}))} data-testid="invoice-gst" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="input-label">GST Mode</label>
                <select className="input-field" value={form.gst_mode} onChange={(e)=>setForm(recalc({...form, gst_mode: e.target.value}))} data-testid="invoice-gst-mode">
                  <option value="forward">Forward (GST added to total)</option>
                  <option value="rcm">RCM — Reverse Charge (GST payable by recipient)</option>
                </select>
              </div>
              <div><label className="input-label">Issue Under (Company on PDF)</label>
                <select className="input-field" value={form.company_brand || "ito_engineering"} onChange={(e)=>setForm({...form, company_brand: e.target.value})} data-testid="invoice-company-brand">
                  {COMPANY_BRANDS.map((b)=><option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <div className="section-title">Customer (Bill To) <span className="text-[11px] font-normal text-[#68665E]">(pick from directory — GST &amp; address auto-fill)</span></div>
            <DirectoryPicker
              testPrefix="invoice-customer"
              kind="all"
              value={{ company: form.customer_name, gst: form.customer_gst, address: form.customer_address, poc_name: form.customer_attn }}
              onSelect={(p)=>setForm((f)=>({...f,
                ...(p.company_id!==undefined?{company_id:p.company_id}:{}),
                ...(p.department_id!==undefined?{department_id:p.department_id}:{}),
                ...(p.contact_id!==undefined?{contact_id:p.contact_id}:{}),
                ...(p.company!==undefined?{customer_name:p.company}:{}),
                ...(p.gst!==undefined?{customer_gst:p.gst}:{}),
                ...(p.address!==undefined?{customer_address:p.address}:{}),
                ...(p.poc_name!==undefined?{customer_attn:p.poc_name}:{}),
              }))}
            />
          </div>

          {unbilled.length > 0 && (
            <div>
              <div className="section-title">Link Lorry Receipts (Optional)</div>
              <div className="border border-[#E5E3DB] max-h-44 overflow-y-auto">
                {unbilled.map((lr) => (
                  <label key={lr.id} className="flex items-center gap-3 px-4 py-2 border-b border-[#E5E3DB] last:border-0 hover:bg-[#FAFAF9] cursor-pointer text-sm">
                    <input type="checkbox" checked={form.lr_ids.includes(lr.id)} onChange={() => toggleLR(lr)} data-testid={`invoice-lr-${lr.id}`} />
                    <span className="font-mono text-xs">{lr.lr_number}</span>
                    <span className="flex-1">{lr.consignor_name} → {lr.consignee_name} ({lr.from_location} → {lr.to_location})</span>
                    <span className="font-medium">{formatINR(lr.freight_amount)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="section-title mb-0">Line Items</div>
              <button type="button" className="btn-ghost text-xs" onClick={addItem} data-testid="invoice-add-item"><Plus className="w-3 h-3" /> Add Item</button>
            </div>
            <div className="border border-[#E5E3DB]">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="table-th">Description</th>
                    <th className="table-th w-20">Qty</th>
                    <th className="table-th w-32">Rate</th>
                    <th className="table-th w-32">Amount</th>
                    <th className="table-th w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, i) => (
                    <tr key={i} className="border-b border-[#E5E3DB] last:border-0">
                      <td className="p-1"><input className="input-field border-0 focus:ring-0 focus:border-0" value={it.description} onChange={(e)=>updateItem(i, "description", e.target.value)} data-testid={`invoice-item-desc-${i}`} /></td>
                      <td className="p-1"><input type="number" step="0.01" className="input-field border-0 focus:ring-0" value={it.quantity} onChange={(e)=>updateItem(i, "quantity", Number(e.target.value))} data-testid={`invoice-item-qty-${i}`} /></td>
                      <td className="p-1"><input type="number" step="0.01" className="input-field border-0 focus:ring-0" value={it.rate} onChange={(e)=>updateItem(i, "rate", Number(e.target.value))} data-testid={`invoice-item-rate-${i}`} /></td>
                      <td className="p-1 px-3 font-medium">{formatINR(it.amount)}</td>
                      <td className="p-1 text-center"><button type="button" onClick={()=>removeItem(i)} className="text-[#8A2526]"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end">
            <div className="w-72 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#68665E]">Subtotal</span><span className="font-medium" data-testid="invoice-subtotal">{formatINR(form.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#68665E]">GST ({form.gst_percent}%){form.gst_mode === "rcm" ? " — RCM, payable by recipient" : ""}</span><span className="font-medium">{formatINR(form.gst_amount)}</span></div>
              <div className="flex justify-between border-t border-[#E5E3DB] pt-2 text-base"><span className="font-bold">Total{form.gst_mode === "rcm" ? " (excl. GST)" : ""}</span><span className="font-bold" data-testid="invoice-total">{formatINR(form.total)}</span></div>
            </div>
          </div>

          <div>
            <label className="input-label">Notes</label>
            <textarea rows={2} className="input-field" value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} data-testid="invoice-notes" />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E3DB]">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="invoice-save-button">{saving ? "Saving..." : (editingId ? "Update Invoice" : "Create Invoice")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
